import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Box,
  InputLabel,
  Typography,
} from "@mui/material";
import { CalendarToday } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useKanban } from "./kanban-provider";


export default function TaskModal({ isOpen, onClose, task }) {
  const { state, addTask, updateTask } = useKanban();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("todo");
  const [priority, setPriority] = useState("medium");
  const [dueDate, setDueDate] = useState(new Date());
  const [assignee, setAssignee] = useState("");
  const [error, setError] = useState("");

  const defaultColumns = useMemo(() => [
    { id: "todo", title: "To Do" },
    { id: "in-progress", title: "In Progress" },
    { id: "done", title: "Done" },
  ], []);
  
  const initialColumns = state.columns?.length > 0 
    ? state.columns 
    : defaultColumns;
    
  useEffect(() => {
    if (isOpen) {
      const activeColumns = state.columns?.length > 0 ? state.columns : defaultColumns;

      if (task) {
        setTitle(task.title);
        setDescription(task.description || "");
        setStatus(task.status);
        setPriority(task.priority || "medium");
        setDueDate(new Date(task.dueDate));
        setAssignee(task.assignee || "Unassigned");
      } else {
        setTitle("");
        setDescription("");
        setStatus(activeColumns[0].id);
        setPriority("medium");
        setDueDate(new Date());
        setAssignee("Unassigned");
      }
      setError("");
    }
  }, [isOpen, task, state.columns, defaultColumns]);

  const handleSubmit = () => {
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    if (!status) {
      setError("Status is required");
      return;
    }
    if (!dueDate || isNaN(dueDate.getTime())) {
      setError("Valid due date is required");
      return;
    }

    const taskData = {
      title,
      description,
      status,
      priority,
      dueDate: dueDate.toISOString(),
      assignee: assignee || "Unassigned",
    };

    if (task) {
      updateTask({ ...taskData, _id: task._id });
    } else {
      addTask(taskData);
    }

    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { maxWidth: 500 } }}
      >
        <DialogTitle>{task ? "Edit Task" : "Create New Task"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
          <FormControl>
            <FormLabel>Title</FormLabel>
            <TextField
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Task title"
              fullWidth
              variant="outlined"
              size="small"
              error={!!error && !title.trim()}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Description</FormLabel>
            <TextField
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Task description"
              fullWidth
              variant="outlined"
              size="small"
              multiline
              rows={3}
            />
          </FormControl>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <FormControl>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                id="status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                label="Status"
                size="small"
                error={!!error && !status}
              >
                {initialColumns?.map((column) => (
                  <MenuItem key={column.id} value={column.id}>
                    {column.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel id="priority-label">Priority</InputLabel>
              <Select
                labelId="priority-label"
                id="priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                label="Priority"
                size="small"
              >
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <FormControl>
              <FormLabel>Due Date</FormLabel>
              <DatePicker
                value={dueDate}
                onChange={(newValue) => setDueDate(newValue)}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    size: "small",
                    error: !!error && (!dueDate || isNaN(dueDate.getTime())),
                    InputProps: {
                      startAdornment: <CalendarToday sx={{ fontSize: 16, mr: 1 }} />,
                    },
                  },
                }}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Assignee</FormLabel>
              <TextField
                id="assignee"
                value={assignee}
                onChange={(e) => setAssignee(e.target.value)}
                placeholder="Assignee name"
                fullWidth
                variant="outlined"
                size="small"
              />
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            variant="outlined" 
            color="secondary"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button 
            variant="outlined" 
            onClick={handleSubmit}
          >
            {task ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}